import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Form, Spinner } from "react-bootstrap";
import Icon from "@mdi/react";
import {
  mdiChevronDown,
  mdiChevronUp,
  mdiClose,
  mdiCreation,
  mdiInformationOutline,
  mdiLogout,
  mdiPlus,
} from "@mdi/js";
import { TrashFill } from "react-bootstrap-icons";
import FsLightbox from "fslightbox-react";
import { useParams } from "react-router-dom";
import { FILE_SIZE_LIMITS } from "../../constants/global-constants";
import {
  addAIProcessingItem,
  aiUpdateItem,
  clearPendingAnnotationRerun,
  deleteItem,
  deleteItemImage,
  enterEdit,
  exitEdit,
  pollTaskStatus,
  setEditDirty,
  setPendingAnnotationRerun,
  updateEditDraft,
  updateItem,
  uploadItemImage,
} from "./slice";
import { registerRetryPayload } from "./retryRegistry";
import { codeKey, draftFromItem } from "./editContent";
import {
  ACCEPTED_IMAGE_TYPES,
  PLURAL_TO_CAPITAL,
  badgeFor,
} from "./editItemFormConstants";
import EditPhotoArea from "./editPhotoArea";
import EditItemFormS4 from "./editItemFormS4";
import EditPhotoMarkup from "./editPhotoMarkup";
import EditConfirmModal from "./editConfirmModal";
import {
  DeleteItemConfirmModal,
  ModifyObsConfirmModal,
  MoveItemModal,
} from "./editItemFormModals";

const EditItemForm = ({ item, onToast }) => {
  const dispatch = useDispatch();
  const { reportId } = useParams();
  const draft = useSelector((s) => s.webReportEdit.edit.draftById[item.id]) || {};
  const isSaving = useSelector((s) => s.webReportEdit.loading.updateItem);
  const isDeleting = useSelector((s) => s.webReportEdit.loading.deleteItem);

  const capitalType = PLURAL_TO_CAPITAL[item.type];
  const isIssue = item.type === "issues";
  const isOverview = item.type === "overviews";
  const badge = badgeFor(item);

  const [observationsExpanded, setObservationsExpanded] = useState(true);

  // S3 = text/photo edit (immediate-commit). S4 = "Modify Observation" mode
  // where the user re-runs AI; primary action becomes Update via AI.
  const [mode, setMode] = useState("S3");
  const [showModifyConfirm, setShowModifyConfirm] = useState(false);
  const [s4ConcernLevel, setS4ConcernLevel] = useState(null);
  const [s4ValidationError, setS4ValidationError] = useState(null);
  const [currentResultExpanded, setCurrentResultExpanded] = useState(false);
  const isAiUpdating = useSelector((s) => s.webReportEdit.loading.aiGenerate);

  // Photo add/delete commit immediately in both S3 and S4 (the observation
  // field) — see AS-890. `removedImageIds` only tracks the optimistic hide for
  // an in-flight delete (reverted on failure); `pendingPhotos` is retained but
  // inert (no flow stages photos anymore).
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [removedImageIds, setRemovedImageIds] = useState(() => new Set());
  const [isDragOver, setIsDragOver] = useState(false);
  const photoInputRef = useRef(null);
  // Snapshot of the item's image-id set when S4 (Modify Observation) opens, so
  // the "Update via AI" button can tell whether photos changed since then.
  const s4InitialImageIdsRef = useRef(null);

  // Reset every state slice when the edited item changes.
  useEffect(() => {
    setPendingPhotos([]);
    setRemovedImageIds(new Set());
    setIsDragOver(false);
    setPendingCodes([]);
    setPendingCodeErrorIds(new Set());
    setMode("S3");
    setS4ConcernLevel(null);
    setS4ValidationError(null);
    setCurrentResultExpanded(false);
  }, [item.id]);

  // Custom code rows added via "+ Add Code". On save, any row with at least
  // one non-empty input is merged into codes_selected.
  const [pendingCodes, setPendingCodes] = useState([]);
  const [uploadsInFlight, setUploadsInFlight] = useState(0);
  const [pendingCodeErrorIds, setPendingCodeErrorIds] = useState(
    () => new Set()
  );

  const handleAddPendingCode = () => {
    setPendingCodes((prev) => [
      ...prev,
      {
        id: `pending-${Date.now()}-${prev.length}`,
        title: "",
        section: "",
        description: "",
      },
    ]);
  };

  const handleUpdatePendingCode = (id, field, value) => {
    setPendingCodes((prev) =>
      prev.map((c) => (c.id === id ? { ...c, [field]: value } : c))
    );
    if (field === "title" && pendingCodeErrorIds.has(id)) {
      setPendingCodeErrorIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleRemovePendingCode = (id) => {
    setPendingCodes((prev) => prev.filter((c) => c.id !== id));
    setPendingCodeErrorIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  useEffect(() => {
    const urls = pendingPhotos.map((p) => p.previewUrl);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [pendingPhotos]);

  const filterValidFiles = (incoming) => {
    const maxSize = FILE_SIZE_LIMITS.IMAGE_MAX_SIZE;
    const accepted = ACCEPTED_IMAGE_TYPES.split(",");
    const valid = [];
    let oversized = 0;
    let invalidType = 0;
    for (const f of incoming) {
      if (!accepted.includes(f.type)) {
        invalidType += 1;
        continue;
      }
      if (f.size > maxSize) {
        oversized += 1;
        continue;
      }
      valid.push(f);
    }
    if (oversized > 0) {
      const noun = oversized === 1 ? "photo is" : "photos are";
      onToast?.({
        message: `${oversized} ${noun} over 10MB and was not added.`,
        variant: "error",
      });
    } else if (invalidType > 0) {
      const noun = invalidType === 1 ? "file is" : "files are";
      onToast?.({
        message: `${invalidType} ${noun} not a supported image (JPEG, PNG, WebP).`,
        variant: "error",
      });
    }
    return valid;
  };

  // Uploads each accepted file immediately so it persists regardless of any
  // subsequent Save/Cancel — applies to both S3 and the S4 observation field.
  const uploadPhotoFilesNow = (incoming) => {
    const valid = filterValidFiles(incoming);
    if (!valid.length) return;
    setS4ValidationError(null);
    setUploadsInFlight((n) => n + valid.length);
    valid.forEach((file) => {
      dispatch(uploadItemImage({ type: capitalType, itemId: item.id, file }))
        .unwrap()
        .then(() => {
          setUploadsInFlight((n) => Math.max(0, n - 1));
          onToast?.({
            message: "Photo added successfully.",
            variant: "success",
          });
        })
        .catch(() => {
          setUploadsInFlight((n) => Math.max(0, n - 1));
          onToast?.({
            message: "Failed to add photo. Please try again.",
            variant: "error",
          });
        });
    });
  };

  const acceptIncomingFiles = (files) => uploadPhotoFilesNow(files);

  const handlePhotoInputChange = (e) => {
    acceptIncomingFiles(Array.from(e.target.files || []));
    e.target.value = "";
  };

  const handleBrowseClick = () => photoInputRef.current?.click();

  const handlePhotoDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handlePhotoDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handlePhotoDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    acceptIncomingFiles(Array.from(e.dataTransfer?.files || []));
  };

  const handleRemovePending = (id) => {
    setPendingPhotos((prev) => prev.filter((p) => p.id !== id));
  };

  // Delete commits immediately in both S3 and S4 (optimistic hide, reverted on
  // error) — see AS-890.
  const handleRemoveExisting = (imageId) => {
    setRemovedImageIds((prev) => {
      const next = new Set(prev);
      next.add(imageId);
      return next;
    });
    dispatch(deleteItemImage({ imageId }))
      .unwrap()
      .then(() => {
        onToast?.({
          message: "Photo deleted successfully.",
          variant: "success",
        });
      })
      .catch(() => {
        setRemovedImageIds((prev) => {
          const next = new Set(prev);
          next.delete(imageId);
          return next;
        });
        onToast?.({
          message: "Failed to delete photo. Please try again.",
          variant: "error",
        });
      });
  };

  const existingImages = (item.images || []).filter(
    (img) => !removedImageIds.has(img.id)
  );

  // Existing image URLs followed by pending preview blobs — same order as
  // the grid so the tile index lines up with the lightbox index.
  const lightboxSources = useMemo(
    () => [
      ...existingImages
        .map((img) => img.annotated_image?.presigned_url || img.presigned_url)
        .filter(Boolean),
      ...pendingPhotos.map((p) => p.previewUrl),
    ],
    [existingImages, pendingPhotos]
  );
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxToggler, setLightboxToggler] = useState(false);
  const [lightboxKey, setLightboxKey] = useState(0);
  const [lightboxRequested, setLightboxRequested] = useState(false);

  useEffect(() => {
    if (lightboxRequested) {
      setLightboxToggler((t) => !t);
      setLightboxRequested(false);
    }
  }, [lightboxKey, lightboxRequested]);

  const openLightbox = (index) => {
    if (!lightboxSources.length) return;
    setLightboxIndex(index);
    setLightboxKey((k) => k + 1);
    setLightboxRequested(true);
  };

  // Keyed on item.id only — silent refetches must not replace the baseline
  // mid-edit. Otherwise `isDirty` would flip false unexpectedly.
  const original = useMemo(
    () => ({
      name: item.name || "",
      description: item.description || "",
      cause: item.cause || "",
      solution: item.solution || "",
      hint: item.hint || "",
      selectedCodeIds: (item.codes_selected || []).map(codeKey),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [item.id]
  );

  const draftSelectedIds = draft.selectedCodeIds ?? original.selectedCodeIds;
  const codeIdsChanged =
    draftSelectedIds.length !== original.selectedCodeIds.length ||
    draftSelectedIds.some((id) => !original.selectedCodeIds.includes(id));
  const filledPendingCodes = pendingCodes.filter(
    (pc) => pc.title.trim() || pc.section.trim() || pc.description.trim()
  );

  const isDirty =
    (draft.name ?? original.name) !== original.name ||
    (draft.description ?? original.description) !== original.description ||
    (draft.hint ?? original.hint) !== original.hint ||
    (isIssue &&
      ((draft.cause ?? original.cause) !== original.cause ||
        (draft.solution ?? original.solution) !== original.solution ||
        codeIdsChanged ||
        filledPendingCodes.length > 0));

  // Publish dirtiness so the page-level navigation guard can prompt before
  // discarding. exitEdit (save/cancel/leave) resets the flag in the slice.
  useEffect(() => {
    dispatch(setEditDirty({ itemId: item.id, isDirty }));
  }, [dispatch, item.id, isDirty]);

  // Auto-grow textareas (including the title) to fit content.
  useEffect(() => {
    document
      .querySelectorAll(".edit-form-textarea, .edit-form-title-input")
      .forEach((el) => {
        el.style.height = "auto";
        el.style.height = `${el.scrollHeight}px`;
      });
  }, [
    draft.name,
    draft.description,
    draft.cause,
    draft.solution,
    draft.hint,
    item.id,
  ]);

  const handleField = (field) => (e) => {
    dispatch(updateEditDraft({ itemId: item.id, field, value: e.target.value }));
  };

  const handleCancel = () => {
    dispatch(exitEdit(item.id));
  };

  const handleSave = () => {
    if (!isDirty) {
      dispatch(exitEdit(item.id));
      return;
    }

    // Any pending row with at least one filled field requires a Title.
    const invalidPendingIds = filledPendingCodes
      .filter((pc) => !pc.title.trim())
      .map((pc) => pc.id);
    if (invalidPendingIds.length > 0) {
      setPendingCodeErrorIds(new Set(invalidPendingIds));
      onToast?.({
        message: "Code Title is required.",
        variant: "error",
      });
      return;
    }
    const fields = {};
    if ((draft.name ?? original.name) !== original.name) {
      fields.name = draft.name;
    }
    if ((draft.description ?? original.description) !== original.description) {
      fields.description = draft.description;
    }
    if ((draft.hint ?? original.hint) !== original.hint) {
      fields.hint = draft.hint;
    }
    if (isIssue) {
      if ((draft.cause ?? original.cause) !== original.cause) {
        fields.cause = draft.cause;
      }
      if ((draft.solution ?? original.solution) !== original.solution) {
        fields.solution = draft.solution;
      }
      // Combine checked AI suggestions with filled custom rows.
      if (codeIdsChanged || filledPendingCodes.length > 0) {
        const fromExisting = [item.code_1, item.code_2, item.code_3].filter(
          (c) => c && draftSelectedIds.includes(codeKey(c))
        );
        const fromPending = filledPendingCodes.map((pc) => ({
          code: pc.title.trim(),
          section: pc.section.trim(),
          content: pc.description.trim(),
        }));
        fields.codes_selected = [...fromExisting, ...fromPending];
      }
    }

    dispatch(updateItem({ type: capitalType, itemId: item.id, fields }))
      .unwrap()
      .then(() => {
        dispatch(exitEdit(item.id));
        onToast?.({ message: "Item saved successfully.", variant: "success" });
      })
      .catch(() => {
        onToast?.({
          message: "Could not save changes. Please try again.",
          variant: "error",
        });
      });
  };

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleRequestDelete = () => setShowDeleteConfirm(true);
  const handleCancelDelete = () => setShowDeleteConfirm(false);

  // Move-modal destinations come from the full template structure so empty
  // sections/subsections are selectable too. Fall back to the populated report
  // sections if all-sections hasn't loaded yet.
  const allSections = useSelector((s) => s.webReportEdit.allSections);
  const reportSections = useSelector(
    (s) => s.webReportEdit.reportContent?.sections || []
  );
  const sections = allSections.length ? allSections : reportSections;
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveSection, setMoveSection] = useState("");
  const [moveSubsection, setMoveSubsection] = useState("");

  const handleOpenMove = () => {
    setMoveSection(item.section || "");
    setMoveSubsection(isOverview ? "" : item.subsection || "");
    setShowMoveModal(true);
  };
  const handleCancelMove = () => setShowMoveModal(false);

  const moveSubsectionsForSection =
    sections.find((s) => s.name === moveSection)?.subsections || [];

  const handleConfirmMove = () => {
    const fields = { section: moveSection };
    if (!isOverview) fields.subsection = moveSubsection;
    dispatch(updateItem({ type: capitalType, itemId: item.id, fields }))
      .unwrap()
      .then(() => {
        setShowMoveModal(false);
        onToast?.({
          message: "Item moved successfully.",
          variant: "success",
        });
      })
      .catch(() => {
        onToast?.({
          message: "Failed to move item. Please try again.",
          variant: "error",
        });
      });
  };

  const moveDisabled =
    !moveSection || (!isOverview && !moveSubsection) || isSaving;

  const handleConfirmDelete = () => {
    dispatch(deleteItem({ type: capitalType, itemId: item.id }))
      .unwrap()
      .then(() => {
        setShowDeleteConfirm(false);
        dispatch(exitEdit(item.id));
        onToast?.({
          message: "Item deleted successfully.",
          variant: "success",
        });
      })
      .catch(() => {
        setShowDeleteConfirm(false);
        onToast?.({
          message: "Could not delete item. Please try again.",
          variant: "error",
        });
      });
  };

  const handleRequestModifyObs = () => setShowModifyConfirm(true);
  const handleCancelModifyObs = () => setShowModifyConfirm(false);

  // S3 → S4 transition discards all unsaved S3 content per spec.
  const handleConfirmModifyObs = () => {
    dispatch(
      enterEdit({
        itemId: item.id,
        type: capitalType,
        draft: draftFromItem(item),
      })
    );
    setPendingPhotos([]);
    setRemovedImageIds(new Set());
    setPendingCodes([]);
    setPendingCodeErrorIds(new Set());
    setS4ConcernLevel(item.concern_level ?? null);
    setCurrentResultExpanded(false);
    s4InitialImageIdsRef.current = new Set(
      (item.images || []).map((img) => img.id)
    );
    setMode("S4");
    setShowModifyConfirm(false);
  };

  // S4 — PATCH /{type}/{id}/async-update/ with keep_image_ids[] + new image
  // Files. The slice handles the BE contract; no separate delete calls.
  const handleUpdateViaAI = async () => {
    const newImages = pendingPhotos.map((p) => p.file);
    const concernLevel = isIssue ? s4ConcernLevel : undefined;
    const descriptionHint = (draft.hint ?? original.hint) || undefined;

    // Limit: ≥1 photo OR ≥1 note. Other types: ≥1 photo. Issue also requires
    // a concerning level. Mirrors the Add-New-Item modal.
    const totalPhotoCount = existingImages.length + newImages.length;
    const hasNote = (descriptionHint || "").trim().length > 0;
    const isLimit = capitalType === "Limit";
    if (isLimit) {
      if (totalPhotoCount === 0 && !hasNote) {
        setS4ValidationError("Add at least 1 photo or 1 note");
        return;
      }
    } else if (totalPhotoCount === 0) {
      setS4ValidationError("Add at least 1 photo");
      return;
    }
    if (isIssue && (concernLevel === null || concernLevel === undefined)) {
      setS4ValidationError("Select a concerning level");
      return;
    }
    setS4ValidationError(null);

    // Kept images render straight from their presigned URLs (annotated
    // version preferred so the processing block reflects what BE will analyze);
    // new files need fresh blob URLs because the form revokes its previewUrls
    // on unmount.
    const keptPhotoUrls = existingImages
      .map((img) => img.annotated_image?.presigned_url || img.presigned_url)
      .filter(Boolean);
    const newPhotoUrls = newImages.map((file) => URL.createObjectURL(file));
    const photoUrls = [...keptPhotoUrls, ...newPhotoUrls];
    const keptImageIds = existingImages.map((img) => img.id).filter(Boolean);

    try {
      const data = await dispatch(
        aiUpdateItem({
          type: capitalType,
          itemId: item.id,
          keptImageIds,
          newImages,
          descriptionHint,
          concernLevel,
        })
      ).unwrap();

      dispatch(
        addAIProcessingItem({
          taskId: data.task_id,
          itemId: data.item_id || item.id,
          type: capitalType,
          concernLevel: concernLevel ?? null,
          photoCount: totalPhotoCount,
          photoUrls,
          displayName: descriptionHint || item.name || null,
          taskState: "PENDING",
          itemStatus: "analyzing",
          error: null,
          displayMode: "inline",
        })
      );
      registerRetryPayload(data.task_id, {
        kind: "edit",
        reportId,
        args: {
          type: capitalType,
          itemId: data.item_id || item.id,
          keptImageIds,
          newImages,
          descriptionHint,
          concernLevel,
        },
      });
      dispatch(pollTaskStatus({ taskId: data.task_id, reportId }));
      // The annotation has now gone through AI analysis — drop the S3 banner.
      dispatch(clearPendingAnnotationRerun(item.id));
      dispatch(exitEdit(item.id));
      onToast?.({
        message: "Observation saved. Keep editing while AI processes.",
        variant: "success",
      });
    } catch (_err) {
      newPhotoUrls.forEach((url) => URL.revokeObjectURL(url));
      onToast?.({
        message: "AI analysis failed to start. Please try again.",
        variant: "error",
      });
    }
  };

  const renderField = (label, field, original_) => (
    <div className="edit-form-field">
      <label className="edit-form-label">{label}</label>
      <Form.Control
        as="textarea"
        rows={1}
        className="edit-form-textarea"
        value={draft[field] ?? original_}
        onChange={handleField(field)}
      />
    </div>
  );

  const codes = isIssue
    ? [item.code_1, item.code_2, item.code_3].filter(Boolean)
    : [];
  const handleToggleCode = (codeId) => {
    const next = draftSelectedIds.includes(codeId)
      ? draftSelectedIds.filter((id) => id !== codeId)
      : [...draftSelectedIds, codeId];
    dispatch(
      updateEditDraft({ itemId: item.id, field: "selectedCodeIds", value: next })
    );
  };

  const observationsTitle = `Observations of ${
    isOverview ? "Overview" : item.name || "Untitled"
  }`;

  // Photos now commit immediately, so compare the current (optimistic) image
  // set against the snapshot taken when S4 opened to know if a re-analysis is
  // warranted. Captures both adds and deletes.
  const s4Snapshot = s4InitialImageIdsRef.current;
  const s4PhotosChanged =
    !!s4Snapshot &&
    (existingImages.length !== s4Snapshot.size ||
      existingImages.some((img) => !s4Snapshot.has(img.id)));
  // True when a photo's annotation was saved/cleared but not yet re-analyzed.
  const showRerunBanner = useSelector(
    (s) => !!s.webReportEdit.pendingAnnotationRerun?.[item.id]
  );
  const s4HasEdits =
    s4PhotosChanged ||
    showRerunBanner ||
    (draft.hint ?? original.hint) !== original.hint ||
    (isIssue && s4ConcernLevel !== (item.concern_level ?? null));

  const [markupImage, setMarkupImage] = useState(null);
  const [showRerunModal, setShowRerunModal] = useState(false);

  const handleRequestRerun = () => setShowRerunModal(true);
  const handleCancelRerun = () => setShowRerunModal(false);

  // Banner-triggered AI re-analyze. Keeps every current image (BE picks the
  // annotated layer where present per Image-API.md), sends no new files, and
  // preserves the item's stored hint / concern level.
  const handleConfirmRerunAI = async () => {
    setShowRerunModal(false);
    const keptImageIds = (item.images || [])
      .map((img) => img.id)
      .filter(Boolean);
    const photoUrls = (item.images || [])
      .map((img) => img.annotated_image?.presigned_url || img.presigned_url)
      .filter(Boolean);

    dispatch(clearPendingAnnotationRerun(item.id));
    try {
      const data = await dispatch(
        aiUpdateItem({
          type: capitalType,
          itemId: item.id,
          keptImageIds,
          newImages: [],
          descriptionHint: item.hint || undefined,
          concernLevel: isIssue ? item.concern_level ?? null : undefined,
        })
      ).unwrap();

      dispatch(
        addAIProcessingItem({
          taskId: data.task_id,
          itemId: data.item_id || item.id,
          type: capitalType,
          concernLevel: isIssue ? item.concern_level ?? null : null,
          photoCount: (item.images || []).length,
          photoUrls,
          displayName: item.hint || item.name || null,
          taskState: "PENDING",
          itemStatus: "analyzing",
          error: null,
          displayMode: "inline",
        })
      );
      registerRetryPayload(data.task_id, {
        kind: "edit",
        reportId,
        args: {
          type: capitalType,
          itemId: data.item_id || item.id,
          keptImageIds,
          newImages: [],
          descriptionHint: item.hint || undefined,
          concernLevel: isIssue ? item.concern_level ?? null : undefined,
        },
      });
      dispatch(pollTaskStatus({ taskId: data.task_id, reportId }));
      dispatch(exitEdit(item.id));
      onToast?.({
        message: "Observation saved. Keep editing while AI processes.",
        variant: "success",
      });
    } catch (_err) {
      dispatch(setPendingAnnotationRerun(item.id));
      onToast?.({
        message: "AI analysis failed to start. Please try again.",
        variant: "error",
      });
    }
  };

  const sharedPhotoProps = {
    existingImages,
    pendingPhotos,
    uploadsInFlight,
    itemName: item.name,
    isDragOver,
    onDragOver: handlePhotoDragOver,
    onDragLeave: handlePhotoDragLeave,
    onDrop: handlePhotoDrop,
    onBrowseClick: handleBrowseClick,
    onFileInputChange: handlePhotoInputChange,
    photoInputRef,
    onRemoveExisting: handleRemoveExisting,
    onRemovePending: handleRemovePending,
    onOpenLightbox: openLightbox,
    onMarkupExisting: setMarkupImage,
  };

  return (
    <div className="edit-form" id={item.id}>
      {mode === "S3" && (
        <>
          <div className="edit-form-header">
            <div className="edit-form-header-left">
              <Button
                variant="outline-secondary"
                size="sm"
                className="edit-form-move-btn"
                onClick={handleOpenMove}
              >
                <Icon path={mdiLogout} size={0.667} />
                Move
              </Button>
              {isOverview ? (
                <span className="edit-form-title-static">Overview</span>
              ) : (
                <Form.Control
                  as="textarea"
                  rows={1}
                  className="edit-form-title-input"
                  value={draft.name ?? original.name}
                  onChange={handleField("name")}
                  placeholder="Untitled"
                />
              )}
            </div>
            {badge && (
              <span className={`edit-item-badge ${badge.className}`}>
                {badge.label}
              </span>
            )}
          </div>

          <div className="edit-form-content">
            {renderField("Description", "description", original.description)}
            {isIssue && renderField("Cause", "cause", original.cause)}
            {isIssue && renderField("Solution", "solution", original.solution)}

            {isIssue && (
              <div className="edit-form-field">
                <label className="edit-form-label">
                  Potential Violation(s)
                </label>
                <div className="edit-form-code-list">
                  {codes.length === 0 && pendingCodes.length === 0 && (
                    <p className="edit-form-code-empty">No codes suggested.</p>
                  )}
                  {codes.map((c) => {
                    const key = codeKey(c);
                    return (
                      <label key={key} className="edit-form-code-row">
                        <input
                          type="checkbox"
                          checked={draftSelectedIds.includes(key)}
                          onChange={() => handleToggleCode(key)}
                          className="edit-form-code-checkbox"
                        />
                        <span>
                          <span className="edit-form-code-heading">
                            {c.code} {c.section}
                          </span>
                          <span className="edit-form-code-content">
                            {c.content}
                          </span>
                        </span>
                      </label>
                    );
                  })}

                  {pendingCodes.map((pc) => (
                    <div key={pc.id} className="edit-form-code-pending">
                      <button
                        type="button"
                        className="edit-form-code-pending-remove"
                        onClick={() => handleRemovePendingCode(pc.id)}
                        aria-label="Remove code"
                      >
                        <Icon path={mdiClose} size={0.667} />
                      </button>
                      <div className="edit-form-code-pending-fields">
                        <div className="edit-form-code-pending-row">
                          <Form.Control
                            type="text"
                            value={pc.title}
                            onChange={(e) =>
                              handleUpdatePendingCode(
                                pc.id,
                                "title",
                                e.target.value
                              )
                            }
                            placeholder="Code Title *"
                            isInvalid={pendingCodeErrorIds.has(pc.id)}
                            className="edit-form-code-pending-title"
                          />
                          <Form.Control
                            type="text"
                            value={pc.section}
                            onChange={(e) =>
                              handleUpdatePendingCode(
                                pc.id,
                                "section",
                                e.target.value
                              )
                            }
                            placeholder="Section Number"
                            className="edit-form-code-pending-section"
                          />
                        </div>
                        <Form.Control
                          type="text"
                          value={pc.description}
                          onChange={(e) =>
                            handleUpdatePendingCode(
                              pc.id,
                              "description",
                              e.target.value
                            )
                          }
                          placeholder="Code Description"
                          className="edit-form-code-pending-description"
                        />
                      </div>
                    </div>
                  ))}

                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="edit-form-add-code-btn"
                    onClick={handleAddPendingCode}
                  >
                    <Icon path={mdiPlus} size={0.667} />
                    Add Code
                  </Button>
                </div>
              </div>
            )}

            <hr className="edit-form-divider" />

            <div className="edit-form-observations">
              <div className="edit-form-observations-header">
                <button
                  type="button"
                  className="edit-form-observations-toggle"
                  onClick={() => setObservationsExpanded((v) => !v)}
                >
                  <Icon
                    path={observationsExpanded ? mdiChevronUp : mdiChevronDown}
                    size={0.75}
                  />
                  <span>Observations</span>
                </button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="edit-form-modify-obs-btn"
                  onClick={handleRequestModifyObs}
                  disabled={isAiUpdating}
                >
                  Modify Observation
                </Button>
              </div>

              {observationsExpanded && (
                <>
                  <div className="edit-form-observations-hint">
                    <Icon path={mdiInformationOutline} size={0.667} />
                    <span>
                      Add short note or annotation for more precise analysis.
                    </span>
                  </div>

                  <EditPhotoArea {...sharedPhotoProps} />

                  {original.hint && (
                    <p className="edit-form-note-readonly">{original.hint}</p>
                  )}

                  {showRerunBanner && (
                    <div className="edit-form-rerun-banner">
                      <Icon
                        path={mdiInformationOutline}
                        size={1}
                        className="edit-form-rerun-banner-icon"
                      />
                      <span className="edit-form-rerun-banner-text">
                        AI works better with annotated photo, analyze again to
                        refine description.
                      </span>
                      <Button
                        size="sm"
                        className="edit-form-rerun-banner-btn"
                        onClick={handleRequestRerun}
                        disabled={isAiUpdating}
                      >
                        <Icon path={mdiCreation} size={0.75} />
                        Update via AI
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}

      {mode === "S4" && (
        <EditItemFormS4
          item={item}
          isIssue={isIssue}
          isOverview={isOverview}
          observationsTitle={observationsTitle}
          photoAreaProps={sharedPhotoProps}
          noteValue={draft.hint ?? original.hint}
          onNoteChange={(e) => {
            handleField("hint")(e);
            if (capitalType === "Limit") setS4ValidationError(null);
          }}
          s4ConcernLevel={s4ConcernLevel}
          onConcernLevelChange={(value) => {
            setS4ConcernLevel(value);
            setS4ValidationError(null);
          }}
          s4ValidationError={s4ValidationError}
          currentResultExpanded={currentResultExpanded}
          setCurrentResultExpanded={setCurrentResultExpanded}
        />
      )}

      <div className="edit-form-footer">
        <Button
          variant="link"
          className="edit-form-delete-btn"
          onClick={handleRequestDelete}
          disabled={isDeleting || isSaving}
          aria-label="Delete item"
        >
          <TrashFill size={20} />
        </Button>
        <div className="edit-form-footer-actions">
          <Button
            variant="outline-secondary"
            onClick={handleCancel}
            disabled={isSaving || isDeleting || isAiUpdating}
          >
            Cancel
          </Button>
          {mode === "S4" ? (
            <Button
              onClick={handleUpdateViaAI}
              disabled={isAiUpdating || isDeleting || !s4HasEdits}
              className="edit-form-update-ai-btn"
            >
              {isAiUpdating ? (
                <>
                  <Spinner animation="border" size="sm" /> Submitting...
                </>
              ) : (
                <>
                  <Icon path={mdiCreation} size={0.75} /> Update via AI
                </>
              )}
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={isSaving || isDeleting}
            >
              {isSaving ? (
                <>
                  <Spinner animation="border" size="sm" /> Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          )}
        </div>
      </div>

      <FsLightbox
        toggler={lightboxToggler}
        sources={lightboxSources}
        sourceIndex={lightboxIndex}
        type="image"
        key={lightboxKey}
      />

      <DeleteItemConfirmModal
        show={showDeleteConfirm}
        onHide={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
      />

      <ModifyObsConfirmModal
        show={showModifyConfirm}
        onHide={handleCancelModifyObs}
        onConfirm={handleConfirmModifyObs}
      />

      <MoveItemModal
        show={showMoveModal}
        onHide={handleCancelMove}
        onConfirm={handleConfirmMove}
        isOverview={isOverview}
        itemName={item.name}
        sections={sections}
        moveSection={moveSection}
        setMoveSection={setMoveSection}
        moveSubsection={moveSubsection}
        setMoveSubsection={setMoveSubsection}
        moveSubsectionsForSection={moveSubsectionsForSection}
        moveDisabled={moveDisabled}
      />

      <EditPhotoMarkup
        show={!!markupImage}
        image={markupImage}
        onClose={() => setMarkupImage(null)}
        onToast={onToast}
      />

      <EditConfirmModal
        show={showRerunModal}
        onHide={handleCancelRerun}
        title="Update result with annotations"
        actions={[
          {
            label: "Cancel",
            variant: "outline-secondary",
            onClick: handleCancelRerun,
          },
          {
            label: "Update via AI",
            variant: "primary",
            className: "edit-form-rerun-confirm-btn",
            onClick: handleConfirmRerunAI,
          },
        ]}
      >
        <p>
          Running a new analysis ensures result reflect the latest changes.
          Note that this will replace current result and manual edits.
        </p>
      </EditConfirmModal>
    </div>
  );
};

export default EditItemForm;
